import { ChangeEvent, useCallback, useMemo, useState } from 'react';
import { useField } from 'react-final-form';
import cn from 'classnames';
import debounce from 'lodash.debounce';

import { TextareaField } from 'UIPackage/MainModule/components/TextareaField';
import { IBooleanSelectOption } from 'KnmPackage/MainModule/interfaces/IBooleanSelectOption';
import { IKnmErknmFormViolation } from 'KnmErknmPackage/ErknmViolationsModule/interfaces/IKnmErknmFormViolation';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { Row } from 'UIPackage/FormModule/components/Row';
import { booleanSelectOptions } from 'KnmPackage/MainModule/constants/booleanSelectOptions';
import { SelectInputField } from 'UIPackage/MainModule/components/SelectInputField';
import { ViolationStatus } from 'KnmErknmPackage/ErknmViolationsModule/components/ViolationStatus';
import { ViolationStatusesEnum } from 'APIPackage/KnmModule/enums/ViolationStatusesEnum';
import { Tooltip } from 'UIPackage/MainModule/components/Tooltip';
import { IconQuestion } from 'UIPackage/IconsModule/components/IconQuestion';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import { IDecisionFormRef } from 'DecisionPackage/MainModule/interfaces/IDecisionFormRef';
import { ErknmDecisionViolationFixItems } from 'DecisionPackage/DecisionViolationModule/components/ErknmDecisionViolationFixItems';
import { IKnmForm } from 'KnmPackage/MainModule/interfaces/IKnmForm';
import { AgreementStatusEnum } from 'APIPackage/KnmModule/enums/AgreementStatusEnum';
import { Button } from 'UIPackage/MainModule/components/Button';
import { IKnmErknmFormEvent } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormEvent';
import {
  createEmptyViolationEvent,
  ErknmActViolationEventItems,
  getAvailablePassportEventOptions,
} from 'KnmErknmPackage/ErknmViolationsModule/components/ErknmActViolationEventItems';
import { createPassportEventSelectOptions } from 'KnmErknmPackage/ErknmViolationsModule/common/createPassportEventSelectOptions';

import { KnmCollapse } from '../../../KnmPackage/MainModule/components/KnmCollapse';
import styles from './ErknmViolationItem.module.scss';
import { convertQuillDeltaToHtml } from '../../../functions/convertQuillDeltaToHtml';
import { IReactQuillDelta } from '../../../APIPackage/KnmModule/interfaces/IReactQuillDelta';
import { checkHasErrorInCollapseBlock } from '../../../functions/checkHasErrorInCollapseBlock';
import { violationSyncRegistry } from '../helpers/violationSyncRegistry';

interface IErknmActViolationItemProps {
  violation: IKnmErknmFormViolation;
  fieldName: string;
  formRef: IKnmFormRef | IDecisionFormRef | undefined;
  watchOnly: boolean;
  isPaperless: boolean;
  violationResolvedOptions: IBooleanSelectOption[];
  id: string;
  isActFirstSigned?: boolean;
  hasUnsignedAct?: boolean;
  isDisabledByDirty?: boolean;
  isKnm?: boolean;
  isPm?: boolean;
  isDecision?: boolean;
  isEnablePaperlessDocument?: boolean;
}

const negativeSelectOption = booleanSelectOptions.find(option => !option.value);

export const ErknmActViolationItem = ({
  violation,
  fieldName,
  formRef,
  watchOnly,
  isPaperless,
  id,
  violationResolvedOptions,
  isActFirstSigned,
  hasUnsignedAct = false,
  isDisabledByDirty = false,
  isKnm,
  isPm,
  isDecision,
  isEnablePaperlessDocument,
}: IErknmActViolationItemProps) => {
  const [isKnmCollapseOpened, setIsKnmCollapseOpened] = useState<boolean>(true);
  const [isShowFullText, setIsShowFullText] = useState(false);

  const isFixedOnPlace: boolean = !!violation.isFixedOnPlace?.value;
  const isShouldBeFixed: boolean = !violation.isUnfixable?.value && !isFixedOnPlace;
  const isNotFixedByStatus: boolean =
    violation.status === ViolationStatusesEnum.NOT_FIXED || violation.status === ViolationStatusesEnum.APPROVING;
  const isFixedByStatus: boolean = violation.status === ViolationStatusesEnum.FIXED;
  const isViolationStatusSuccessfullyAppealed: boolean =
    violation.status === ViolationStatusesEnum.SUCCESSFULLY_APPEALED;

  const agreements: IKnmForm['agreements'] = useField('agreements' as keyof IKnmForm, { subscription: { value: true } })
    .input.value;

  let passportEventsFieldName: 'eventsPm' | 'events' | 'eventsErknm' = 'eventsErknm';

  if (isPm) {
    passportEventsFieldName = 'eventsPm';
  } else if (isDecision) {
    passportEventsFieldName = 'events';
  }

  const {
    input: { value: passportEvents },
  } = useField(passportEventsFieldName as keyof IKnmForm, { subscription: { value: true } });
  const passportEventsList: IKnmErknmFormEvent[] | undefined = passportEvents;

  const onChangeIsFixedOnPlace = useCallback(
    (v: IBooleanSelectOption) => {
      if (!v.value && violation.explanation) {
        // @ts-ignore
        formRef?.current?.change(`${fieldName}.explanation` as keyof IKnmForm);
      }
      if (v.value === true) {
        // @ts-ignore
        formRef?.current.change(`${fieldName}.includeInSubject`, false);
      }
    },
    [violation.explanation],
  );

  const isNeedShowIsViolationResolved = useMemo(() => {
    return violation.isUnfixable?.id === 1 && violation.isFixedOnPlace?.id === 1;
  }, [violation]);

  const handleViolationResolvedChange = useCallback(
    (v: IBooleanSelectOption) => {
      if (isPaperless) {
        // @ts-ignore
        formRef?.current.change(`${fieldName}.isFixed`, v);
        if (v.value === false) {
          // @ts-ignore
          formRef?.current.change(`${fieldName}.fixes`);
        }
        violationSyncRegistry.syncField(
          violation.id || '0',
          `${fieldName}.isViolationResolved`,
          'isViolationResolved',
          v,
        );
      }
    },
    [formRef, isPaperless],
  );

  const handleViolationResolvedNoteChange = useCallback(
    (value: string) => {
      if (isPaperless) {
        violationSyncRegistry.syncField(
          violation.id || '0',
          `${fieldName}.isViolationResolvedNote`,
          'isViolationResolvedNote',
          value,
        );
      }
    },
    [isPaperless, violation.id, fieldName],
  );

  const debouncedHandleViolationResolvedNoteChange = useMemo(() => debounce(handleViolationResolvedNoteChange, 1000), [
    handleViolationResolvedNoteChange,
  ]);

  const handleViolationResolvedNoteOnTextareaChange = useCallback(
    (e: ChangeEvent<HTMLTextAreaElement>) => {
      const { value } = e.target;

      debouncedHandleViolationResolvedNoteChange(value);
    },
    [debouncedHandleViolationResolvedNoteChange],
  );

  const handleIsFixedChange = useCallback(
    (v: IBooleanSelectOption) => {
      if (isPaperless) {
        // @ts-ignore
        formRef?.current.change(`${fieldName}.isViolationResolved`, v);
        if (v.value === false) {
          // @ts-ignore
          formRef?.current.change(`${fieldName}.fixes`);
        }
        if (v.value === true) {
          // @ts-ignore
          formRef?.current.change(`${fieldName}.includeInSubject`, false);
        }
        violationSyncRegistry.syncField(violation.id || '0', `${fieldName}.isFixed`, 'isFixed', v);
        violationSyncRegistry.syncField(
          violation.id || '0',
          `${fieldName}.isViolationResolved`,
          'isViolationResolved',
          v,
        );
      }
    },
    [formRef, isPaperless],
  );

  const availablePassportEventOptions = useMemo(() => getAvailablePassportEventOptions(violation, passportEventsList), [
    violation,
    passportEventsList,
  ]);

  const handleHasViolationEvent = useCallback(
    (v: IBooleanSelectOption) => {
      // @ts-ignore
      formRef?.current.change(`${fieldName}.inEvent`, v);

      if (v?.value) {
        const firstOption = createPassportEventSelectOptions(passportEventsList)[0] || null;

        // @ts-ignore
        formRef?.current.change(`${fieldName}.events`, [createEmptyViolationEvent(firstOption)]);
      } else {
        // @ts-ignore
        formRef?.current.change(`${fieldName}.events`, undefined);
      }
    },
    [formRef, fieldName, passportEventsList],
  );

  const handleAddViolationEvent = useCallback(() => {
    const nextOption = availablePassportEventOptions[0];

    if (!nextOption) {
      return;
    }

    formRef?.current.mutators.push(`${fieldName}.events`, createEmptyViolationEvent(nextOption));
  }, [availablePassportEventOptions, formRef, fieldName]);

  const hasError = isKnmCollapseOpened && checkHasErrorInCollapseBlock(formRef, fieldName);

  /**
   * Определение занятости нарушения в соглашениях
   */
  const isDisabledByAgreement = useMemo(() => {
    if (!Array.isArray(agreements)) {
      return false;
    }

    const availableAgreements = agreements?.filter(
      agreement =>
        agreement.status === AgreementStatusEnum.DRAFT ||
        agreement.status === AgreementStatusEnum.READY_TO_APPROVE ||
        agreement.status === AgreementStatusEnum.ON_APPROVAL ||
        agreement.status === AgreementStatusEnum.APPROVED ||
        agreement.status === AgreementStatusEnum.PENDING_TERMINATION ||
        agreement.status === AgreementStatusEnum.COMPLETED,
    );

    if (!availableAgreements || availableAgreements.length === 0) {
      return false;
    }

    let isDisabled = false;

    availableAgreements.forEach(agreement => {
      agreement.violations?.forEach(agrViolation => {
        agrViolation.violatedMandatory.forEach(vm => {
          vm.violations?.forEach(vi => {
            if (vi.id === violation.id) {
              isDisabled = true;
            }
          });
        });
      });
    });

    return isDisabled;
  }, [agreements, violation]);

  const canAddViolationEvent =
    !!violation.inEvent?.value &&
    availablePassportEventOptions.length > 0 &&
    !!hasUnsignedAct &&
    !(watchOnly || isDisabledByDirty || isDisabledByAgreement);

  /**
   * Отображаемое поле для компонента "Нарушение устранено"
   */
  const isFixedFieldName = useMemo(() => {
    if (violation.repeatCorrected?.value) return 'repeatCorrected';

    if (violation.repeatCorrectedInEvent?.value) return 'repeatCorrectedInEvent';

    return 'isFixed';
  }, [violation]);

  const { convertedHtml, fullHtml, isTruncated } = useMemo(() => {
    if (violation.descriptionReactQuillEditor && typeof violation.descriptionReactQuillEditor === 'object') {
      try {
        const html = convertQuillDeltaToHtml(violation.descriptionReactQuillEditor as IReactQuillDelta);
        const MAX_HTML_LENGTH = 2000;

        if (html.length > MAX_HTML_LENGTH) {
          return {
            convertedHtml: html.substring(0, MAX_HTML_LENGTH),
            fullHtml: html,
            isTruncated: true,
          };
        }

        return {
          convertedHtml: html,
          fullHtml: html,
          isTruncated: false,
        };
      } catch (error) {
        console.error('Error converting Quill delta to HTML:', error);

        return {
          convertedHtml: '',
          fullHtml: '',
          isTruncated: false,
        };
      }
    }

    return {
      convertedHtml: null,
      fullHtml: null,
      isTruncated: false,
    };
  }, [violation.descriptionReactQuillEditor]);

  const handleToggleFullText = useCallback(() => {
    setIsShowFullText(prev => !prev);
  }, []);

  return (
    <KnmCollapse
      id={id}
      title={
        <span className={`${styles.ItemCollapseTitle} shared-break-word`}>
          <span>Нарушение №{violation.number}:</span>{' '}
          {convertedHtml ? (
            <>
              <div
                /* eslint-disable-next-line react/no-danger */
                dangerouslySetInnerHTML={{
                  __html: isShowFullText ? fullHtml : convertedHtml,
                }}
                className="ql-editor ql-editor-preview-block"
                style={{ whiteSpace: 'pre-wrap' }}
              />
              {isTruncated && (
                <span className={styles.ShowMoreButtonWrapper} onClick={e => e.stopPropagation()}>
                  <button
                    type="button"
                    className={styles.ShowMoreButton}
                    onClick={e => {
                      e.stopPropagation();
                      e.preventDefault();
                      handleToggleFullText();
                    }}
                    onMouseDown={e => e.stopPropagation()}
                  >
                    {isShowFullText ? 'Скрыть' : 'Показать полностью'}
                  </button>
                </span>
              )}
            </>
          ) : (
            <>«{violation.title}»</>
          )}
        </span>
      }
      bodyClassName={styles.ItemCollapseBody}
      headerPostfix={<ViolationStatus status={violation.status} />}
      error={hasError}
      changeOpenedCallback={setIsKnmCollapseOpened}
    >
      <>
        {isShouldBeFixed && (
          <Row>
            <FieldBlock
              label={
                <Row align="center" margin="none" notSpace>
                  <span>Нарушение устранено</span>
                  <Tooltip
                    placement="right"
                    content={
                      <div className={styles.TooltipContent}>
                        Поле доступно для изменения, если статус нарушения имеет значение «Не устранено» или «Ожидает
                        подтверждения». Значение «Да» в поле устанавливается в случае, если устранение произошло после
                        проведения мероприятия.
                      </div>
                    }
                  >
                    <IconQuestion className={styles.TooltipIcon} />
                  </Tooltip>
                </Row>
              }
              placementLabel="left"
              color={isPaperless ? 'black' : undefined}
              className={cn({ [styles.FieldBlock]: isPaperless })}
              labelSize="col-3"
              labelFontSize="small"
              type="not-space"
              id={`${id}.${isFixedFieldName}`}
            >
              <FieldBlock size={!isPaperless ? 'col-3' : undefined}>
                <SelectInputField
                  className={isPaperless ? '' : 'shared-row-field'}
                  name={`${fieldName}.${isFixedFieldName}`}
                  options={booleanSelectOptions}
                  isClearable={false}
                  defaultValue={negativeSelectOption}
                  disabled={!hasUnsignedAct || watchOnly || isDisabledByDirty || isDisabledByAgreement}
                  onChange={handleIsFixedChange}
                />
              </FieldBlock>
            </FieldBlock>
          </Row>
        )}
        {isPaperless && (
          <Row>
            <FieldBlock
              label="Выявлено в рамках действия"
              placementLabel="left"
              color="black"
              className={styles.FieldBlock}
              labelSize="col-3"
              labelFontSize="small"
              type="not-space"
              id={`${id}.inEvent`}
              button={
                canAddViolationEvent ? (
                  <Button
                    className="shared-btn-add"
                    text="Добавить"
                    type="button"
                    variant="link"
                    color="primary"
                    onClick={handleAddViolationEvent}
                  />
                ) : undefined
              }
            >
              <SelectInputField
                name={`${fieldName}.inEvent`}
                options={booleanSelectOptions}
                isClearable={false}
                defaultValue={negativeSelectOption}
                disabled={
                  !hasUnsignedAct ||
                  watchOnly ||
                  isDisabledByDirty ||
                  isDisabledByAgreement ||
                  !passportEventsList?.length
                }
                onChange={handleHasViolationEvent}
              />
            </FieldBlock>
          </Row>
        )}
        {isPaperless && violation?.inEvent?.value && (
          <ErknmActViolationEventItems
            name={fieldName}
            formRef={formRef}
            passportEvents={passportEventsList}
            watchOnly={watchOnly || isDisabledByDirty || isDisabledByAgreement}
            hasUnsignedAct={hasUnsignedAct}
            isKnm={isKnm}
            isPm={isPm}
            isDecision={isDecision}
            id={id}
          />
        )}
        {isShouldBeFixed && (
          <Row>
            <FieldBlock
              label={
                <Row align="center" margin="none" notSpace>
                  <span>Нарушение обжаловано</span>
                  <Tooltip
                    placement="right"
                    content={
                      <div className={styles.TooltipContent}>
                        Поле доступно для изменения, если статус нарушения имеет значение «Не устранено» или «Ожидает
                        подтверждения». Значение «Да» в поле устанавливается в случае, если нарушение было обжаловано в
                        пользу заявителя.
                      </div>
                    }
                  >
                    <IconQuestion className={styles.TooltipIcon} />
                  </Tooltip>
                </Row>
              }
              placementLabel="left"
              color={isPaperless ? 'black' : undefined}
              className={cn({ [styles.FieldBlock]: isPaperless })}
              labelSize="col-3"
              labelFontSize="small"
              type="not-space"
            >
              <FieldBlock size={!isPaperless ? 'col-3' : undefined}>
                <SelectInputField
                  className={isPaperless ? '' : 'shared-row-field'}
                  name={`${fieldName}.isAppealed`}
                  options={booleanSelectOptions}
                  isClearable={false}
                  disabled={
                    !(isNotFixedByStatus || hasUnsignedAct) || watchOnly || isDisabledByDirty || isDisabledByAgreement
                  }
                  defaultValue={negativeSelectOption}
                />
              </FieldBlock>
            </FieldBlock>
          </Row>
        )}
        <Row>
          <FieldBlock
            label="Нарушение не подлежит устранению"
            placementLabel="left"
            color={isPaperless ? 'black' : undefined}
            className={cn({ [styles.FieldBlock]: isPaperless })}
            labelSize="col-3"
            labelFontSize="small"
            type="not-space"
          >
            <FieldBlock size={!isPaperless ? 'col-3' : undefined}>
              <SelectInputField
                className={isPaperless ? '' : 'shared-row-field'}
                name={`${fieldName}.isUnfixable`}
                options={booleanSelectOptions}
                isClearable={false}
                defaultValue={negativeSelectOption}
                disabled={!hasUnsignedAct}
              />
            </FieldBlock>
          </FieldBlock>
        </Row>
        <Row>
          <FieldBlock
            label="Нарушение устранено в ходе мероприятия"
            placementLabel="left"
            color={isPaperless ? 'black' : undefined}
            className={cn({ [styles.FieldBlock]: isPaperless })}
            labelSize="col-3"
            labelFontSize="small"
            type="not-space"
            id={`${id}.isFixedOnPlace`}
          >
            <FieldBlock size={!isPaperless ? 'col-3' : undefined}>
              <SelectInputField
                className={isPaperless ? '' : 'shared-row-field'}
                name={`${fieldName}.isFixedOnPlace`}
                options={booleanSelectOptions}
                isClearable={false}
                defaultValue={negativeSelectOption}
                onChange={onChangeIsFixedOnPlace}
                disabled={!hasUnsignedAct}
              />
            </FieldBlock>
          </FieldBlock>
        </Row>
        {isPaperless && isNeedShowIsViolationResolved && isActFirstSigned && (
          <>
            <FieldBlock
              label={'Факт устранения выявленного нарушения'}
              margin="top"
              placementLabel="left"
              className={styles.FieldBlock}
              color="black"
            >
              <SelectInputField
                name={`${fieldName}.isViolationResolved`}
                options={violationResolvedOptions}
                disabled={
                  isFixedByStatus || isDisabledByDirty || isDisabledByAgreement || isViolationStatusSuccessfullyAppealed
                }
                isClearable={false}
                onChange={handleViolationResolvedChange}
              />
            </FieldBlock>
            {violation?.isViolationResolved?.value && (
              <FieldBlock
                label={'Сведения о факте устранения выявленного нарушения'}
                margin="top"
                placementLabel="left"
                className={cn(styles.FieldBlock, {
                  [styles.GroupMarginBottom]: !isFixedOnPlace && violation.isFixed?.value,
                })}
                color="black"
              >
                <TextareaField
                  name={`${fieldName}.isViolationResolvedNote`}
                  disabled={isFixedByStatus || isDisabledByDirty || isDisabledByAgreement}
                  placeholder={'Сведения о факте устранения выявленного нарушения'}
                  onChange={handleViolationResolvedNoteOnTextareaChange}
                />
              </FieldBlock>
            )}
          </>
        )}
        {isFixedOnPlace && (
          <Row>
            <FieldBlock
              label="Пояснение"
              placementLabel="left"
              labelSize="col-3"
              labelFontSize="small"
              type="not-space"
              className={cn(styles.FieldBlock, styles.GroupMarginBottom)}
              color="black"
            >
              <TextareaField
                className={isPaperless ? '' : 'shared-row-field'}
                name={`${fieldName}.explanation`}
                disabled={!hasUnsignedAct}
                placeholder="Введите пояснение (при необходимости)"
                autoHeight
              />
            </FieldBlock>
          </Row>
        )}
        {((isEnablePaperlessDocument && isPaperless && violation.isFixed?.value) ||
          violation.repeatCorrected?.value ||
          violation.repeatCorrectedInEvent?.value) && (
          <ErknmDecisionViolationFixItems
            violation={violation}
            formRef={formRef}
            name={fieldName}
            isFixedFieldName={isFixedFieldName}
            watchOnly={watchOnly}
            isPaperless={isPaperless}
            inAct
            isDisabledByDirty={isDisabledByDirty}
            isDisabledByAgreement={isDisabledByAgreement}
            hasUnsignedAct={hasUnsignedAct}
            isKnm={isKnm}
            isPm={isPm}
            isDecision={isDecision}
            id={id}
            isFixed={violation.isFixed?.value}
          />
        )}
      </>
    </KnmCollapse>
  );
};
